import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Operation } from 'fast-json-patch'

import { useFetch } from '../useFetch'
import { useAuth } from '../../contexts/AuthContext'

import { CONFIG } from '../../config'
import { BlockModel } from '../../types/court/court'
import { ApiError } from '../../types/exception/errorStatus'
import { getErrorMessage } from '../../utils/exception/exceptionUtils'
import { formatDateTimesInText } from '../../utils/dateUtils'

export function useUpdateBlock() {
  const { t, i18n } = useTranslation(['blocks', 'exceptions'])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const { authInfo } = useAuth()
  const { patcher } = useFetch(authInfo?.accessToken, authInfo?.refreshToken)

  const resetState = () => {
    setLoading(false)
    setError(null)
    setSuccess(null)
  }

  const updateBlock = async (
    courtId: number,
    blockId: number,
    patch: Operation[],
    /** Confirms cancelling overlapping bookings (with the block's reason). */
    cancelConflicts = false
  ) => {
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const query = cancelConflicts ? '?cancelConflicts=true' : ''
      const endpoint = `${CONFIG.BOUNCE_API}/api/v1/courts/${courtId}/blocks/${blockId}${query}`
      const updatedBlock = await patcher<BlockModel>(
        endpoint,
        patch,
        'application/json-patch+json'
      )

      if (!updatedBlock) {
        return
      }

      setSuccess(t('blocks:blocks.successfully-updated'))
      return updatedBlock
    } catch (error) {
      console.error(error)

      if (error instanceof ApiError) {
        setError(
          formatDateTimesInText(getErrorMessage(error.data), i18n.language)
        )
        return
      }

      setError(t('exceptions:unknown'))
    } finally {
      setLoading(false)
    }
  }

  return {
    updateBlock,
    loading,
    error,
    success,
    resetState,
  }
}
